import React from "react";
import { Link } from "react-router-dom";
import {
    CircleCheck,
    ArrowRight
} from "lucide-react";

import { welcomeConfig, WelcomeType, routePrefixMap } from "./welcomeConfig";
import { getAuthCookie } from "../../utils/authCookies";

interface QuickProps {
    label: string;
    icon: React.ReactNode;
    to?: string;
    bg?: string;
}

const getItemHref = (
    item: { generatePath?: (prefix: string) => string; path?: string },
    prefix: string
) => (item.generatePath ? item.generatePath(prefix) : item.path);

const WelcomeScreen = () => {

    // -------------------------------------------------------------------
    // GET LICENSE TYPE
    // -------------------------------------------------------------------
    let rawType = getAuthCookie("licenseType") || "SIGNAGE";
    console.log("License Type:", rawType);
    rawType = rawType.toUpperCase();

    const type = rawType as WelcomeType;
    const data = welcomeConfig[type];

    const prefix = routePrefixMap[type] || "signage";

    // SAFETY
    if (!data) return <div>Invalid License Type</div>;

    return (
        <div style={{ background: "#ffffffe6", minHeight: "100vh" }}>

            <div className="container py-4">
                {/* ------------------ HERO SECTION ------------------ */}
                <div
                    className="rounded-4 p-5 mb-5 text-center shadow-sm"
                    style={{
                        background:
                            "linear-gradient(90deg, #0d5bff 0%, #1e6bff 100%)",
                        color: "#ffffff",
                        borderRadius: "20px"
                    }}
                >
                    {/* Welcome Back pill */}
                    <div
                        className="px-3 py-1 rounded-pill mb-3 mx-auto"
                        style={{
                            background: "color-mix(in oklab, #fff 10%, transparent)",
                            width: "fit-content",
                            fontSize: "15px",
                            fontWeight: 800,
                            color: "#fff"
                        }}
                    >
                        ✨ Welcome Back
                    </div>

                    {/* TITLE */}
                    <h1
                        className="fw-bold mb-3"
                        style={{
                            fontSize: "45px",
                            color: "#ffffff"
                        }}
                    >
                        {data.heroTitle}
                    </h1>

                    {/* DESCRIPTION BOX */}
                    <div
                        className="mx-auto p-3 rounded-3 fw-bold"
                        style={{
                            maxWidth: "700px",
                            background: "color-mix(in oklab, #fff 10%, transparent)",
                            border: "1px solid rgba(255,255,255,0.3)",
                            color: "#ffffff",
                            fontSize: "16px",
                            lineHeight: "24px"
                        }}
                    >
                        {data.heroSubtitle}
                    </div>
                </div>
                {/* ------------------ WHAT YOU CAN DO ------------------ */}
                <h5 className="fw-bold mb-4 fs-2">
                    <span>
                        What You Can Do Here
                    </span>
                    <div className="mt-2" style={{ width: "65px", height: "5px", backgroundColor: "#155dfc" }}></div>
                </h5>

                <div className="row g-4 mb-5">
                    {data.whatYouCanDo?.map((item: any, idx: number) => {
                        const to = getItemHref(item, prefix);

                        return (
                        <div className="col-md-4" key={idx}>
                            {to ? (
                            <Link
                                to={to}
                                className="shadow-border-1 box-shadow-datatables p-4 bg-white h-100 d-block text-decoration-none text-reset welcome-feature-card-link"
                                style={{
                                    border: "1px solid #ebe6e7",
                                    cursor: "pointer",
                                }}
                            >
                                {/* ICON */}
                                <div
                                    style={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 16,
                                        background: item.iconBg,
                                        border: item.iconBorder,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginBottom: 16,
                                    }}
                                >
                                    {item.icon}
                                </div>

                                {/* TITLE */}
                                <h6 className="fw-bold fs-16 mb-2" style={{ color: "#111827" }}>
                                    {item.title}
                                </h6>

                                {/* DESCRIPTION (desc OR text) */}
                                {item.desc && (
                                    <p className="fs-13 small mb-2">{item.desc}</p>
                                )}

                                {item.text && (
                                    <p className="fs-13 small mb-2">{item.text}</p>
                                )}

                                {/* ITEMS LIST (green ticks) */}
                                {item.items && (
                                    <ul className="list-unstyled mt-2">
                                        {item.items.map((it: string, i: number) => (
                                            <li
                                                key={i}
                                                className="d-flex align-items-start gap-2 mb-2"
                                                style={{ fontSize: "14px", color: "#374151" }}
                                            >
                                                <CircleCheck size={18} color="#22c55e" strokeWidth={2.3} />
                                                {it}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Link>
                            ) : (
                            <div
                                className="shadow-border-1 box-shadow-datatables p-4 bg-white h-100"
                                style={{ border: "1px solid #ebe6e7" }}
                            >
                                {/* ICON */}
                                <div
                                    style={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 16,
                                        background: item.iconBg,
                                        border: item.iconBorder,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginBottom: 16,
                                    }}
                                >
                                    {item.icon}
                                </div>

                                <h6 className="fw-bold fs-16 mb-2" style={{ color: "#111827" }}>
                                    {item.title}
                                </h6>

                                {item.desc && (
                                    <p className="fs-13 small mb-2">{item.desc}</p>
                                )}

                                {item.text && (
                                    <p className="fs-13 small mb-2">{item.text}</p>
                                )}

                                {item.items && (
                                    <ul className="list-unstyled mt-2">
                                        {item.items.map((it: string, i: number) => (
                                            <li
                                                key={i}
                                                className="d-flex align-items-start gap-2 mb-2"
                                                style={{ fontSize: "14px", color: "#374151" }}
                                            >
                                                <CircleCheck size={18} color="#22c55e" strokeWidth={2.3} />
                                                {it}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            )}
                        </div>
                        );
                    })}

                    {/* {data.whatYouCanDo?.map((item: any, idx: number) => (
                        <div className="col-md-4" key={idx}>
                            <div className="card border-0 shadow-sm rounded-4 p-4 h-100" style={{ border:" 1px solid #ebe6e7" }}>
                                <div
                                    style={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 16,
                                        background: item.iconBg,
                                        border: item.iconBorder,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginBottom: 16,
                                    }}
                                >
                                    {item.icon}
                                </div>

                                <h6 className="fw-semibold mb-2" style={{ color: "#111827" }}>
                                    {item.title}
                                </h6>

                                <p className="text-muted small mb-0">{item.desc}</p>
                            </div>
                        </div>
                    ))} */}
                </div>

                {/* ------------------ TIPS (HIDDEN FOR AGENT) ------------------ */}
                {!data.hideTips && (
                    <>
                        <h5 className="fw-bold mb-4 fs-2">
                            <span>
                                Tips to Boost Productivity
                            </span>
                            <div className="mt-2" style={{ width: "65px", height: "5px", backgroundColor: "#155dfc" }}></div>
                        </h5>

                        <div className="d-flex flex-column gap-4 mb-5">
                            {data.tips?.map((tip: any, idx: number) => (
                                <TipCard key={idx} {...tip} />
                            ))}
                        </div>
                    </>
                )}

                {/* ------------------ QUICK ACTIONS ------------------ */}
                <h5 className="fw-bold mb-4 fs-2">
                    <span>
                        Quick Actions
                    </span>
                    <div className="mt-2" style={{width:"65px",height:"5px",backgroundColor:"#155dfc"}}></div>
                </h5>

                <div className="shadow-border-1 box-shadow-datatables p-4 bg-white h-100 mb-5">
                    <ul className="list-unstyled m-0">
                        {data.quickActions?.map((a: any, idx: number) => (
                                <QuickItem
                                    key={idx}
                                    label={a.label}
                                    icon={a.icon}
                                    bg={a.bg}
                                    to={getItemHref(a, prefix)}
                                />
                            ))}
                    </ul>

                    {/* <ul className="list-unstyled m-0">
                        {data.quickActions?.map((a: any, idx: number) => (
                            <QuickItem
                                key={idx}
                                label={a.label}
                                icon={a.icon}
                                bg={a.bg}
                                // action={() => navigate(a.path)}
                                action={() => navigate(a.path, { state: { fromQuick: true } })}
                            />
                        ))}
                    </ul> */}
                </div>

                {/* ------------------ FOOTER ------------------ */}
                <div
                    className="rounded-4 text-center text-white p-4 mb-5"
                    style={{
                        background: "linear-gradient(90deg, #0c1b2e 0%, #13233e 100%)",
                        borderRadius: "20px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    }}
                >
                    <h6 className="fw-bold mb-2" style={{ fontSize: "20px", color: "#ffffff" }}>
                        Ready to get started?
                    </h6>

                    <p
                        className="m-0"
                        style={{
                            opacity: 0.85,
                            fontSize: "16px",
                            color: "#ffffff",
                        }}
                    >
                        Choose a quick action above or explore the menu on the left to manage your signage operations.
                    </p>
                </div>
            </div>
            <style>
                {
                    ` 
                      .quick-item:hover {
  background: #f8faff;
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.quick-item:hover .quick-icon {
  transform: scale(1.05);
  background: #e8f0ff;
}

.quick-item:hover span {
  color: #0d5bff;
}

.quick-item:hover .arrow-icon {
  transform: translateX(6px);
  color: #0d5bff !important;
}

.welcome-feature-card-link {
  transition: 0.2s ease;
}

.welcome-feature-card-link:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08) !important;
  transform: translateY(-2px);
}

                    `
                }
            </style>
        </div>
    );
};

/* ------------------ SUB COMPONENTS ------------------ */

const QuickItem: React.FC<QuickProps> = ({ label, icon, to, bg }) => {
    const rowContent = (
        <>
            <div className="d-flex align-items-center gap-3">
                <div
                    className="quick-icon"
                    style={{
                        width: 42,
                        height: 42,
                        borderRadius: 14,
                        background: bg || "#f0f4ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.25s ease",
                    }}
                >
                    {icon}
                </div>

                <span
                    className="fw-semibold"
                    style={{
                        fontSize: "15px",
                        color: "#111827",
                        transition: "color 0.25s ease",
                    }}
                >
                    {label}
                </span>
            </div>

            <ArrowRight
                size={20}
                className="arrow-icon"
                style={{
                    color: "#6b7280",
                    transition: "transform 0.25s ease, color 0.25s ease",
                }}
            />
        </>
    );

    const rowStyle = {
        padding: "20px 24px",
        color: "inherit" as const,
    };

    return (
        <li
            className={to ? "quick-item" : ""}
            style={{
                listStyle: "none",
                borderBottom: "1px solid #f1f1f1",
                borderRadius: "14px",
                transition: to ? "all 0.25s ease" : undefined,
            }}
        >
            {to ? (
                <Link
                    to={to}
                    className="d-flex align-items-center justify-content-between text-decoration-none text-reset"
                    style={{ ...rowStyle, cursor: "pointer" }}
                >
                    {rowContent}
                </Link>
            ) : (
                <div
                    className="d-flex align-items-center justify-content-between"
                    style={rowStyle}
                >
                    {rowContent}
                </div>
            )}
        </li>
    );
};


/* ---------------------------------------------
      PERFECT FIGMA-MATCHED TIP CARD
---------------------------------------------- */
const TipCard = ({
    icon,
    bg,
    title,
    text,
    items,
    note,
    bordercolor,
}: {
    icon: React.ReactNode;
    bg: string;
    title: string;
    text?: string;
    items?: string[];
    note?: string;
    bordercolor?: string;
}) => (
    <div
        className="p-4 rounded-4 shadow-sm mb-4"
        style={{
            background: bg,
            border: `3px solid ${bordercolor || "transparent"}`,
            borderRadius: "20px",
        }}
    >
        {/* ROW: ICON + TITLE + SUBTITLE (Left-aligned, same as screenshot) */}
        <div className="d-flex gap-3 align-items-start mb-2">

            {/* ICON BOX */}
            <div
                style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    background: "#ffffff",
                    border: "1px solid rgba(0,0,0,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {icon}
            </div>

            {/* TITLE + SUBTITLE */}
            <div>
                <h6 className="fw-bold mb-3" style={{ color: "#111827", fontSize: "17px" }}>
                    {title}
                </h6>

                {text && (
                    <p
                        className="mb-2"
                        style={{
                            fontSize: "14px",
                            marginTop: "-2px",
                            lineHeight: "20px",
                        }}
                    >
                        {text}
                    </p>
                )}
            </div>
        </div>

        {/* BULLET LIST ITEMS */}
        {items && (
            <ul className="list-unstyled mb-3" style={{ paddingLeft: "65px" }}>
                {items.map((item, idx) => (
                    <li
                        key={idx}
                        className="d-flex align-items-center gap-2 mb-2"
                        style={{ fontSize: "14px", color: "#374151" }}
                    >
                        <CircleCheck size={17} color="#22c55e" strokeWidth={2.3} />
                        {item}
                    </li>
                ))}
            </ul>
        )}

        {/* FOOTNOTE / NOTE */}
        {note && (
            <div
                className="p-3 br-8 small fw-bold fs-14"
                style={{
                    background: "#ffffff",
                    border: "1px solid rgba(0,0,0,0.08)",
                    marginTop: "10px",
                    marginLeft: "65px",
                    color: "#444",
                }}
            >
                💡 {note}
            </div>
        )}
    </div>
);



export default WelcomeScreen;
